import React from 'react';
import { EvalSectionData, StandardizedTestScore, Client } from '../../types';
import { ageAtEvaluation, AUTHORED_EVAL_TREATMENT_SETTINGS, SPEECH_EVALUATION_CPTS } from '../../utils/clinicalDocDisplay';
import {
  FileText,
  Target,
  Calendar,
  Layers,
  Plus,
  Trash2,
  ShieldCheck,
  Award,
  BookOpen,
  UserCheck,
  CheckCircle2,
  Activity,
  HeartHandshake,
  AlertCircle
} from 'lucide-react';

interface EvalWorkspaceProps {
  data: EvalSectionData;
  onChange: (updated: EvalSectionData) => void;
  client?: Client;
  readOnly?: boolean;
}

export const EvalWorkspace: React.FC<EvalWorkspaceProps> = ({
  data,
  onChange,
  client,
  readOnly = false
}) => {
  const updateField = <K extends keyof EvalSectionData>(field: K, value: EvalSectionData[K]) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  // Standardized Tests
  const tests: StandardizedTestScore[] = data.standardized_tests || [];
  const addTest = () => {
    updateField('standardized_tests', [
      ...tests,
      {
        test_name: '',
        date_administered: new Date().toISOString().split('T')[0],
        standard_score: '',
        percentile: '',
        age_equivalent: '',
        interpretation: ''
      }
    ]);
  };
  const removeTest = (idx: number) => {
    updateField('standardized_tests', tests.filter((_, i) => i !== idx));
  };
  const updateTest = (idx: number, field: keyof StandardizedTestScore, val: any) => {
    const updated = [...tests];
    updated[idx] = { ...updated[idx], [field]: val };
    updateField('standardized_tests', updated);
  };

  // Canonical Goals
  const establishedGoals = data.established_goals || [];
  const addGoal = () => {
    updateField('established_goals', [
      ...establishedGoals,
      {
        description: '',
        baseline: '',
        target_percentage: 80,
        target_date: '12 months'
      }
    ]);
  };
  const removeGoal = (idx: number) => {
    updateField('established_goals', establishedGoals.filter((_, i) => i !== idx));
  };
  const updateGoal = (idx: number, field: string, val: any) => {
    const updated = [...establishedGoals];
    updated[idx] = { ...updated[idx], [field]: val };
    updateField('established_goals', updated);
  };

  const setDiscipline = (discipline: 'SPEECH' | 'FEEDING') => {
    if (discipline === 'FEEDING') {
      onChange({ ...data, discipline, eval_cpt_code: '92610', cpt_code: '92526' });
      return;
    }
    const currentEval = String(data.eval_cpt_code || '');
    const speechEval = (SPEECH_EVALUATION_CPTS as readonly string[]).includes(currentEval) ? currentEval : '92523';
    onChange({ ...data, discipline: 'SPEECH', eval_cpt_code: speechEval, cpt_code: '92507' });
  };
  const feeding = data.discipline === 'FEEDING';
  const evaluationAge = ageAtEvaluation(client?.dob, data.evaluation_date);

  return (
    <div className="space-y-6 text-slate-200">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Discipline</span>
        {(['SPEECH', 'FEEDING'] as const).map((choice) => (
          <button
            key={choice}
            type="button"
            disabled={readOnly}
            onClick={() => setDiscipline(choice)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${
              (feeding ? 'FEEDING' : 'SPEECH') === choice
                ? 'bg-sky-600 text-white border-sky-500'
                : 'bg-[#0a101b] text-slate-200 border-slate-600'
            }`}
          >
            {choice === 'SPEECH' ? 'Speech' : 'Feeding'}
          </button>
        ))}
        {feeding ? (
          <span className="text-[10px] text-slate-400">Evaluation CPT 92610 · Treatment CPT 92526</span>
        ) : (
          <label className="text-[10px] text-slate-400 flex items-center gap-2">
            Evaluation CPT
            <select
              disabled={readOnly}
              value={data.eval_cpt_code || '92523'}
              onChange={(e) => onChange({ ...data, discipline: 'SPEECH', eval_cpt_code: e.target.value, cpt_code: '92507' })}
              className="bg-[#0a101b] border border-slate-600 rounded px-2 py-1 text-xs text-slate-100"
            >
              {SPEECH_EVALUATION_CPTS.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
            <span>Treatment CPT 92507</span>
          </label>
        )}
      </div>
      {/* 1. Client Context & Safety Precautions */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <BookOpen className="w-4 h-4 text-sky-400" />
          <span>1. Client Context, Primary Language & Safety Considerations</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              Primary Language in Home Environment
            </label>
            <input
              type="text"
              disabled={readOnly}
              value={data.primary_language || client?.primary_language || 'English'}
              onChange={(e) => updateField('primary_language', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              Safety Considerations, Allergies, or Seizures
            </label>
            <input
              type="text"
              disabled={readOnly}
              value={data.safety_precautions || ''}
              onChange={(e) => updateField('safety_precautions', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-slate-200 mb-1">
            Setting & Evaluation Circumstances Narrative
          </label>
          <textarea
            disabled={readOnly}
            rows={2}
            value={data.client_context || ''}
            onChange={(e) => updateField('client_context', e.target.value)}
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
          />
        </div>
      </div>

      {/* 2. Referral Reason, Caregiver Concerns vs Direct Observations */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <HeartHandshake className="w-4 h-4 text-sky-400" />
          <span>2. Referral Reason, Caregiver Concerns vs. Direct Observations</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1 uppercase tracking-wider">
              Caregiver / Parent Reported Concerns
            </label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={data.caregiver_concerns || ''}
              onChange={(e) => updateField('caregiver_concerns', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1 uppercase tracking-wider">
              Direct Clinician Behavioral & Communication Observations
            </label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={data.direct_observations || ''}
              onChange={(e) => updateField('direct_observations', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>

      {/* 3. Medical, Developmental, Educational & Therapy History */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <Activity className="w-4 h-4 text-sky-400" />
          <span>3. Medical, Developmental, Educational & Therapy History</span>
        </div>
        <textarea
          disabled={readOnly}
          rows={3}
          value={data.medical_history || ''}
          onChange={(e) => updateField('medical_history', e.target.value)}
          className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
        />
      </div>

      {/* 4. Comprehensive Communication Assessment (6 Functional Domains) */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-4">
        <div className="flex items-center justify-between pb-2 border-b border-slate-700/60">
          <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider">
            <Layers className="w-4 h-4 text-sky-400" />
            <span>{feeding ? '4. Feeding and Swallowing Findings' : '4. Comprehensive Communication Assessment (Functional Domains)'}</span>
          </div>
        </div>

        {feeding ? (
          <textarea
            disabled={readOnly}
            rows={5}
            value={data.feeding_findings || ''}
            onChange={(e) => updateField('feeding_findings', e.target.value)}
            placeholder="Oral intake, swallowing, textures, utensils, and safety findings"
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
          />
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          {/* Receptive Language */}
          <div className="p-3.5 bg-[#121c2e] rounded-xl border border-slate-700/70 space-y-1.5">
            <span className="font-bold text-white block">Receptive Language</span>
            <textarea
              disabled={readOnly}
              rows={2}
              value={data.receptive_language || ''}
              onChange={(e) => updateField('receptive_language', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded p-2 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>

          {/* Expressive Language */}
          <div className="p-3.5 bg-[#121c2e] rounded-xl border border-slate-700/70 space-y-1.5">
            <span className="font-bold text-white block">Expressive Language</span>
            <textarea
              disabled={readOnly}
              rows={2}
              value={data.expressive_language || ''}
              onChange={(e) => updateField('expressive_language', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded p-2 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>

          {/* Speech Production & Intelligibility */}
          <div className="p-3.5 bg-[#121c2e] rounded-xl border border-slate-700/70 space-y-1.5">
            <span className="font-bold text-white block">Speech Production & Intelligibility</span>
            <textarea
              disabled={readOnly}
              rows={2}
              value={data.speech_production || ''}
              onChange={(e) => updateField('speech_production', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded p-2 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>

          {/* Pragmatic / Social Communication */}
          <div className="p-3.5 bg-[#121c2e] rounded-xl border border-slate-700/70 space-y-1.5">
            <span className="font-bold text-white block">Pragmatic & Social Interaction</span>
            <textarea
              disabled={readOnly}
              rows={2}
              value={data.pragmatic_social || ''}
              onChange={(e) => updateField('pragmatic_social', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded p-2 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>

          {/* AAC & Multimodal Communication */}
          <div className="p-3.5 bg-[#121c2e] rounded-xl border border-slate-700/70 space-y-1.5">
            <span className="font-bold text-white block">AAC & Multimodal Communication</span>
            <textarea
              disabled={readOnly}
              rows={2}
              value={data.aac_multimodal || ''}
              onChange={(e) => updateField('aac_multimodal', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded p-2 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>

          {/* Oral-Motor & Voice */}
          <div className="p-3.5 bg-[#121c2e] rounded-xl border border-slate-700/70 space-y-1.5">
            <span className="font-bold text-white block">Oral Mechanism, Voice & Fluency</span>
            <textarea
              disabled={readOnly}
              rows={2}
              value={data.oral_motor_voice || ''}
              onChange={(e) => updateField('oral_motor_voice', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded p-2 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
        )}
      </div>

      {/* 5. Standardized Evaluations & Scores */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-slate-700/60">
          <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider">
            <Award className="w-4 h-4 text-sky-400" />
            <span>5. Standardized & Objective Evaluations</span>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={addTest}
              className="px-2.5 py-1 rounded bg-sky-950/60 hover:bg-sky-900/60 text-sky-300 border border-sky-500/40 text-[11px] font-bold flex items-center space-x-1 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add Standardized Test</span>
            </button>
          )}
        </div>

        {tests.length === 0 ? (
          <div className="p-4 rounded-lg bg-[#0a101b] border border-dashed border-slate-700/80 text-center text-slate-300 text-xs">
            No standardized tests added yet. Click "+ Add Standardized Test" to record formal metrics.
          </div>
        ) : (
          <div className="space-y-3">
            {tests.map((t, idx) => (
              <div key={idx} className="p-3.5 bg-[#121c2e] rounded-xl border border-slate-700/70 space-y-2.5">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-slate-200 text-xs">Test #{idx + 1}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removeTest(idx)}
                      className="text-rose-400 hover:text-rose-300 p-1 rounded hover:bg-rose-950/40"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5 text-xs">
                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-semibold text-slate-200 mb-1">Test Instrument</label>
                    <input
                      type="text"
                      disabled={readOnly}
                      value={t.test_name}
                      onChange={(e) => updateTest(idx, 'test_name', e.target.value)}
                      className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-200 mb-1">Standard Score (Mean 100)</label>
                    <input
                      type="text"
                      disabled={readOnly}
                      value={t.standard_score || ''}
                      onChange={(e) => updateTest(idx, 'standard_score', e.target.value)}
                      className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-200 mb-1">Percentile Rank (%)</label>
                    <input
                      type="text"
                      disabled={readOnly}
                      value={t.percentile || ''}
                      onChange={(e) => updateTest(idx, 'percentile', e.target.value)}
                      className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-200 mb-1">Clinical Interpretation</label>
                  <input
                    type="text"
                    disabled={readOnly}
                    value={t.interpretation || ''}
                    onChange={(e) => updateTest(idx, 'interpretation', e.target.value)}
                    className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 6. Diagnostic Impressions & Medical Necessity */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-amber-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <ShieldCheck className="w-4 h-4 text-amber-400" />
          <span>6. Diagnostic Impressions & Medicaid Medical Necessity (AHCCCS)</span>
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-amber-200 mb-1">
            Clinical Diagnostic Summary & ICD-10 Justification
          </label>
          <textarea
            disabled={readOnly}
            rows={2}
            value={data.diagnostic_impressions || ''}
            onChange={(e) => updateField('diagnostic_impressions', e.target.value)}
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 focus:outline-none leading-relaxed shadow-inner disabled:bg-slate-900/80 disabled:text-slate-500"
          />
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-amber-200 mb-1">
            Skilled Need Rationale & Impact on Activities of Daily Living (ADL)
          </label>
          <textarea
            disabled={readOnly}
            rows={3}
            value={data.medical_necessity_rationale || ''}
            onChange={(e) => updateField('medical_necessity_rationale', e.target.value)}
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 focus:outline-none leading-relaxed italic shadow-inner disabled:bg-slate-900/80 disabled:text-slate-500"
          />
        </div>
      </div>

      {/* 7. Initial Plan of Care: Measurable Treatment Goals */}
      <div
        id="eval-section-goals"
        className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b border-slate-700/60">
          <div className="flex flex-wrap items-center gap-2 text-cyan-400 font-bold text-xs uppercase tracking-wider">
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-cyan-400" />
              <span>7. Initial Plan of Care: Treatment Goals</span>
            </div>
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-700 uppercase tracking-wider">
              Required for Treatment
            </span>
            <span
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                establishedGoals.length === 0
                  ? 'bg-amber-50 text-amber-900 border-amber-300'
                  : 'bg-slate-50 text-slate-700 border-slate-300'
              }`}
            >
              {establishedGoals.length} {establishedGoals.length === 1 ? 'Goal' : 'Goals'}
            </span>
          </div>
          {!readOnly && (
            <button
              type="button"
              id="add-canonical-goal-btn"
              onClick={addGoal}
              className="px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white shadow-xs text-xs font-bold transition-colors"
            >
              Add Goal
            </button>
          )}
        </div>

        {establishedGoals.length === 0 ? (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-center space-x-2 font-semibold">
            <AlertCircle className="w-4 h-4 text-amber-800 shrink-0" />
            <span>Treatment Goal Required</span>
          </div>
        ) : (
          <div className="space-y-3">
            {establishedGoals.map((g, idx) => (
              <div key={idx} className="p-3.5 bg-[#121c2e] rounded-xl border border-slate-700/70 space-y-2.5">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-slate-100 text-xs">Goal #{idx + 1}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removeGoal(idx)}
                      className="text-rose-400 hover:text-rose-300 p-1 rounded hover:bg-rose-950/40"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <label className="block text-[10px] font-semibold text-slate-200">Goal designation</label>
                <select
                  disabled={readOnly}
                  value={g.goal_term || ''}
                  onChange={(e) => updateGoal(idx, 'goal_term', e.target.value)}
                  className="w-full sm:w-48 bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100"
                >
                  <option value="">Not designated</option>
                  <option value="LONG">Long-term</option>
                  <option value="SHORT">Short-term</option>
                </select>
                <textarea
                  disabled={readOnly}
                  rows={2}
                  value={g.description}
                  onChange={(e) => updateGoal(idx, 'description', e.target.value)}
                  className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-200 mb-1">Baseline Performance</label>
                    <input
                      type="text"
                      disabled={readOnly}
                      value={g.baseline || ''}
                      onChange={(e) => updateGoal(idx, 'baseline', e.target.value)}
                      className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-200 mb-1">Mastery Target (%)</label>
                    <input
                      type="number"
                      disabled={readOnly}
                      value={g.target_percentage || 80}
                      onChange={(e) => updateGoal(idx, 'target_percentage', Number(e.target.value))}
                      className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-200 mb-1">Anticipated Timeframe</label>
                    <input
                      type="text"
                      disabled={readOnly}
                      value={g.target_date || '12 months'}
                      onChange={(e) => updateGoal(idx, 'target_date', e.target.value)}
                      className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 8. Treatment Plan & Prescribed Dosage */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <Calendar className="w-4 h-4 text-sky-400" />
          <span>8. Treatment Plan & Prescribed Dosage (Plan of Care Period)</span>
        </div>

        <div id="eval-section-setting" className="space-y-2">
          <label className="block text-[10px] font-semibold text-slate-200">Evaluation setting</label>
          <p className="text-[10px] text-slate-400">Required. Home / Community or Teletherapy cannot be assumed.</p>
          <div className="flex flex-wrap items-center gap-2">
            {AUTHORED_EVAL_TREATMENT_SETTINGS.map((choice) => (
              <button
                key={choice}
                type="button"
                disabled={readOnly}
                onClick={() => updateField('treatment_setting', choice)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${
                  data.treatment_setting === choice
                    ? 'bg-sky-600 text-white border-sky-500'
                    : 'bg-[#0a101b] text-slate-200 border-slate-600'
                }`}
              >
                {choice}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Evaluation Date</label>
            <input
              type="date"
              disabled={readOnly}
              value={data.evaluation_date || ''}
              onChange={(e) => updateField('evaluation_date', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
            <p className="mt-1 text-[10px] text-slate-400">
              {evaluationAge === null ? 'Age prints when date of birth and this date are both stored.' : `Age at evaluation: ${evaluationAge}`}
            </p>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Encounter Start</label>
            <input
              type="time"
              disabled={readOnly}
              value={data.time_in || ''}
              onChange={(e) => updateField('time_in', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Encounter Stop</label>
            <input
              type="time"
              disabled={readOnly}
              value={data.time_out || ''}
              onChange={(e) => updateField('time_out', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Prescribed Frequency</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.prescribed_frequency || '2x/week'}
              onChange={(e) => updateField('prescribed_frequency', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-semibold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Duration per Session</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.prescribed_duration || (data.duration_minutes ? `${data.duration_minutes} minutes` : (client?.expected_duration_minutes ? `${client.expected_duration_minutes} minutes` : '60 minutes'))}
              onChange={(e) => updateField('prescribed_duration', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-semibold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">POC Start Date</label>
            <input
              type="date"
              disabled={readOnly}
              value={data.poc_start_date || new Date().toISOString().split('T')[0]}
              onChange={(e) => {
                const newStart = e.target.value;
                const d = new Date(newStart);
                d.setFullYear(d.getFullYear() + 1);
                const newEnd = d.toISOString().split('T')[0];
                updateField('poc_start_date', newStart);
                if (!data.poc_end_date) {
                  updateField('poc_end_date', newEnd);
                }
              }}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">POC End Date</label>
            <input
              type="date"
              disabled={readOnly}
              value={
                data.poc_end_date ||
                (() => {
                  const d = new Date(data.poc_start_date || Date.now());
                  d.setFullYear(d.getFullYear() + 1);
                  return d.toISOString().split('T')[0];
                })()
              }
              onChange={(e) => updateField('poc_end_date', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>

      {/* 9. Home Program & Caregiver Education Plan */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <HeartHandshake className="w-4 h-4 text-sky-400" />
          <span>9. Home Program Strategies & Caregiver Education Plan</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1 uppercase tracking-wider">
              Functional Carryover Strategies for Daily Routines
            </label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={data.home_program_strategies || ''}
              onChange={(e) => updateField('home_program_strategies', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1 uppercase tracking-wider">
              Caregiver Coaching & Training Objectives
            </label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={data.caregiver_training_plan || ''}
              onChange={(e) => updateField('caregiver_training_plan', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>

      {/* 10. Prognosis, Discharge Criteria & Recommendations */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-slate-700/60">
          <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider">
            <Award className="w-4 h-4 text-sky-400" />
            <span>10. Prognosis, Discharge Criteria & Additional Recommendations</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Rehabilitation Prognosis</label>
            <select
              disabled={readOnly}
              value={data.prognosis || 'Good'}
              onChange={(e) => updateField('prognosis', e.target.value as any)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-semibold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            >
              <option value="Excellent">Excellent</option>
              <option value="Good">Good</option>
              <option value="Fair">Fair</option>
              <option value="Guarded">Guarded</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              Prognostic Indicators
            </label>
            <input
              type="text"
              disabled={readOnly}
              value={data.prognostic_indicators || ''}
              onChange={(e) => updateField('prognostic_indicators', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-slate-200 mb-1">
            Anticipated Discharge Criteria
          </label>
          <input
            type="text"
            disabled={readOnly}
            value={data.discharge_criteria || ''}
            onChange={(e) => updateField('discharge_criteria', e.target.value)}
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
          />
        </div>
      </div>

      {/* 11. Ordering Physician Certification & Prescription Details */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] text-white border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <UserCheck className="w-4 h-4 text-sky-400" />
          <span>11. Ordering Physician Prescription & Certification Block</span>
        </div>
        <p className="text-[11px] text-slate-300 leading-relaxed">
          Physician details are preserved on the client's chart and rendered on the PDF certification statement sent via Faxage electronic fax path.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Physician Name *</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.physician_name || client?.physician_name || ''}
              onChange={(e) => updateField('physician_name', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none font-semibold disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Physician NPI (10 digits)</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.physician_npi || client?.physician_npi || ''}
              onChange={(e) => updateField('physician_npi', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none font-mono font-bold disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Physician Fax (for POC routing)</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.physician_fax || client?.physician_fax || ''}
              onChange={(e) => updateField('physician_fax', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none font-mono font-bold disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
